<script setup lang="ts">
import { computed, ref } from 'vue';
import {
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
} from '@headlessui/vue';
import {
  autoUpdate,
  flip,
  offset,
  shift,
  size,
  useFloating,
} from '@floating-ui/vue';
import type { TitleSelectProps } from './title-select';
import HugeIcon from '../HugeIcon/HugeIcon.vue';
import { FLOATING_OVERLAY_Z_INDEX } from '../layering/layering';

const VIEWPORT_PADDING = 8;
const MIN_AVAILABLE_HEIGHT = 0;
const LISTBOX_OFFSET = 4;

const props = withDefaults(defineProps<TitleSelectProps>(), {
  variant: 'default',
  hideSelectedItemIcon: false,
});

const isInlineVariant = computed(() => props.variant === 'inline');

const emit = defineEmits<{
  selectItem: [id: string];
}>();

const selectedItem = computed(() => {
  return props.items.find((i) => i.id === props.selectedId);
});

const listboxButtonRef = ref<HTMLElement | null>(null);
const listboxOptionsRef = ref<HTMLElement | null>(null);

const { floatingStyles } = useFloating(listboxButtonRef, listboxOptionsRef, {
  placement: 'bottom-start',
  strategy: 'fixed',
  whileElementsMounted: autoUpdate,
  middleware: [
    offset(LISTBOX_OFFSET),
    flip(),
    shift({ padding: VIEWPORT_PADDING }),
    size({
      padding: VIEWPORT_PADDING,
      apply({ availableHeight, elements }): void {
        const maxHeight = Math.max(MIN_AVAILABLE_HEIGHT, availableHeight);
        elements.floating.style.maxHeight = `${String(maxHeight)}px`;
        elements.floating.style.overflowY = 'auto';
      },
    }),
  ],
});
</script>

<template>
  <Listbox
    :model-value="selectedItem"
    @update:model-value="emit('selectItem', $event.id)"
  >
    <div :class="isInlineVariant ? 'relative min-w-0 flex-1' : 'relative mt-1'">
      <ListboxButton
        ref="listboxButtonRef"
        :class="
          isInlineVariant
            ? 'flex w-full min-w-0 items-center gap-2 text-left text-base font-semibold text-slate-900'
            : 'flex items-center text-xl font-semibold'
        "
      >
        <HugeIcon
          v-if="isInlineVariant"
          class="shrink-0 text-slate-500"
          icon="chevron_down"
          :size="20"
        />
        <span
          v-if="isInlineVariant"
          class="flex min-w-0 flex-1 items-center overflow-hidden"
        >
          <span class="inline-flex max-w-full min-w-0 items-center gap-2">
            <span class="truncate">
              {{ selectedItem?.label }}
            </span>
            <HugeIcon
              v-if="!props.hideSelectedItemIcon"
              class="shrink-0 text-slate-500"
              icon="folder"
              :size="20"
            />
          </span>
        </span>
        <span v-else>
          {{ selectedItem?.label }}
        </span>
        <HugeIcon v-if="!isInlineVariant" class="ml-2" icon="chevron_down" />
      </ListboxButton>

      <Teleport to="body">
        <ListboxOptions
          ref="listboxOptionsRef"
          :style="{ ...floatingStyles, zIndex: FLOATING_OVERLAY_Z_INDEX }"
          class="w-max min-w-48 rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black/5 focus:outline-none sm:text-sm"
        >
          <ListboxOption
            v-for="item in props.items"
            v-slot="{ active, selected }"
            :key="item.id"
            :value="item"
            as="template"
          >
            <li
              :class="[
                active ? 'bg-primary-50 text-primary-500' : 'text-gray-900',
                'relative cursor-pointer py-2 pr-4 pl-10 select-none',
              ]"
            >
              <span
                :class="[
                  selected ? 'font-medium' : 'font-normal',
                  'block truncate',
                ]"
                >{{ item.label }}</span
              >
              <span
                v-if="selected"
                class="text-primary-500 absolute inset-y-0 left-0 flex items-center pl-3"
              >
                <HugeIcon icon="checkmark" />
              </span>
            </li>
          </ListboxOption>
        </ListboxOptions>
      </Teleport>
    </div>
  </Listbox>
</template>
